import type { GradeCategory } from "@core/grades/types";
import { cn } from "@design-system/lib/utils";
import { trans } from "@mongez/localization";
import { Link } from "@remix-run/react";
import { ArrowUpLeft, BookOpen } from "lucide-react";
import { useCategoryFeedTile } from "../../hooks/use-category-feed-tile.hook";

export function CategoryFeedTile({
  category,
  index,
}: {
  category: GradeCategory;
  index: number;
}) {
  const tile = useCategoryFeedTile(category, index);

  if (tile.hasImage) {
    return (
      <Link
        to={tile.href}
        className="group relative min-h-48 overflow-hidden rounded-[1.6rem] bg-slate-900 shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-xl">
        <img
          src={tile.image}
          alt={category.name}
          className="absolute inset-0 h-full w-full object-cover transition duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/20 to-transparent" />
        <span className="absolute end-4 top-4 flex h-9 w-9 items-center justify-center rounded-full border border-white/20 bg-black/25 text-white backdrop-blur">
          <ArrowUpLeft className="h-4 w-4 rtl:rotate-0 ltr:rotate-90" />
        </span>
        <div className="absolute inset-x-0 bottom-0 p-5 text-white">
          <h2 className="line-clamp-2 text-lg font-black">{category.name}</h2>
          <p className="mt-1 text-xs font-bold text-white/75">
            {trans("socialHome.topicLessons", {
              count: tile.lessonCount,
            })}
          </p>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={tile.href}
      className={cn(
        "group relative min-h-48 overflow-hidden rounded-[1.6rem] border border-white/70 bg-gradient-to-br p-5 shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-xl dark:border-slate-800",
        tile.surface,
      )}>
      <span
        className={cn(
          "absolute -end-3 -top-8 text-[8rem] font-black opacity-10",
          tile.accent,
        )}>
        {tile.sequence}
      </span>
      <span className="relative flex h-12 w-12 items-center justify-center rounded-2xl bg-white/80 shadow-sm backdrop-blur dark:bg-slate-900/80">
        <BookOpen className={cn("h-6 w-6", tile.accent)} />
      </span>
      <div className="relative mt-7">
        <h2 className="line-clamp-2 text-lg font-black text-slate-950 dark:text-white">
          {category.name}
        </h2>
        <p className="mt-2 text-xs font-bold text-slate-500 dark:text-slate-400">
          {trans("socialHome.topicLessons", {
            count: tile.lessonCount,
          })}
        </p>
      </div>
    </Link>
  );
}
